/**
 * ceo-chat.tsx — top-bar CEO chat widget.
 *
 * Spec ref: §6 (CEO orchestrator) + §11 (UI sitemap: "CEO chat icon (right)
 * — opens CEO chat drawer").
 *
 * Click the icon → Sheet (slide-over) opens with chat. UI connects to
 * /ws/ceo/chat (cookie-auth, gateway bridges to the orchestrator container).
 * Streams text tokens, displays tool_use announcements inline.
 *
 * Persistence: the conversation is durable SERVER-SIDE in
 * chaos.conductor_messages (account-scoped) — the source of truth, loaded on
 * open via GET /api/ceo/conversation so any device shows the same chat. The
 * localStorage copy under `chaos.ceo-chat.<userId>` is only an optimistic
 * cache for instant first paint (server wins).
 */
import {
  Bot,
  Send,
  Wrench,
  AlertTriangle,
  Loader2,
  MoreVertical,
  History as HistoryIcon,
  RotateCcw,
  Undo2,
  X,
  Mic,
  Square,
  ExternalLink,
  ListChecks,
} from 'lucide-react';
import { Link, useRouter } from '@tanstack/react-router';
import * as React from 'react';

import { Button } from '../ui/button';
import { CopyButton } from '../ui/copy-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { ApiError, apiUpload, isPermanentUploadRefusal } from '../../lib/api/client';
import { voicePersist } from '../../lib/voice-persist';
import { useIsMobile } from '../../lib/use-media-query';
import { env } from '../../env';
import {
  ConductorActivity,
  conductorArtifactSummary,
  conductorArtifactsSignature,
  humanizeToolName,
  type ConductorActivityKind,
  type ConductorArtifact,
} from './conductor-activity';

// ── Markdown, lazily ─────────────────────────────────────────────────
//
// FOUNDER COMPLAINT, 2026-07-26: "The reply renders with NO formatting at all
// and is completely unreadable — this is a total disaster. Bring it to the same
// look we have in a session."
//
// He is right and this file was the whole cause: `{turn.content}` as a raw
// React text child in a `whitespace-pre-wrap` div, zero markdown imports in the
// module. The Conductor's own charter (system-prompt.ts HARD_CHARTER) INSTRUCTS
// it to emit markdown receipts with bold titles and deep links, and it complies
// — the UI then threw the markdown away and printed the asterisks.
//
// ⚠ React.lazy, NEVER a static import. <CeoChat> is mounted in the app layout
// (shell.tsx → activity-bar.tsx), so a static import pulls the ~340 KB gz
// markdown-vendor chunk into the shell of EVERY page in CHAOS. This is exactly
// how session-chat-pane.tsx:198 does it, and going through MarkdownContent
// (rather than react-markdown directly) inherits its legacy-browser
// short-circuit for free: isLegacyRegexBrowser() → PlainTextFallback and the
// vendor chunk is never even requested, which is what keeps the founder's iPad
// Air 1 working.
const MarkdownContent = React.lazy(() => import('../agent/markdown-content'));

// The account-global entry reuses the exact same interactive transport as a
// card chat. Keep it lazy: CeoChat is mounted in the application shell, while
// the session pane should load only after the drawer opens for a provisioned
// Conductor.
const ConductorSessionChat = React.lazy(() =>
  import('./conductor-session-chat').then((mod) => ({ default: mod.ConductorSessionChat })),
);

// ── Conductor orchestrator ───────────────────────────────────────────
//
// 2026-06-13 (founder spec, "Conductor"): the orchestrator chat no longer
// exposes a model or reasoning-effort picker. The backend
// (claude-subprocess.ts runCodex) is hardwired to ALWAYS run the best
// model + highest reasoning (xhigh) regardless of what the client sends,
// so there is nothing left to choose here. The WS turn now carries just
// the message ({ message }); model/effort are no longer sent.

// ── Conductor receipt deep-links (spec 05 §4.1) ──────────────────────
//
// After chaos.create_task succeeds, the Conductor's reply embeds a deep-link
// to the new card so the founder can click through. The orchestrator stream
// only forwards `tool_use` (name) + `tool_result` (ok) — NOT the structured
// receipt body — so the link is recovered from the assistant's prose.
//
// The create-task endpoint emits `/board/<boardId>?card=<cardId>`, but the
// live router opens a card via the PATH route `/board/$boardId/card/$cardId`
// (the `?card=` query is dropped by the board route's validateSearch — every
// in-app surface uses the path form). So we accept BOTH shapes and route to
// the canonical path form (GAP12 — verified against routeTree).
interface CardDeepLink {
  boardId: string;
  cardId: string;
}

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
// `/board/<uuid>?card=<uuid>` (backend deepLink) OR `/board/<uuid>/card/<uuid>` (path form).
const DEEP_LINK_RE = new RegExp(
  `/board/(${UUID})(?:\\?card=(${UUID})|/card/(${UUID}))`,
  'g',
);

function extractCardLinks(text: string): CardDeepLink[] {
  if (!text) return [];
  const out: CardDeepLink[] = [];
  const seen = new Set<string>();
  for (const m of text.matchAll(DEEP_LINK_RE)) {
    const boardId = m[1];
    const cardId = m[2] ?? m[3];
    if (!boardId || !cardId) continue;
    const key = `${boardId}/${cardId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ boardId, cardId });
  }
  return out;
}

/**
 * ── WHAT THE FOUR CONDUCTOR STATES ARE FOR ─────────────────────────────────
 *
 * Before `conductorState` existed the drawer received one bit — a `cardHref`
 * that was either a string or null — and rendered a dead amber banner for the
 * null. That banner is what the founder saw: it told him, in prose, to POST to
 * /api/conductor/provision, and the drawer had no control that called it. The
 * composer beneath it is disabled too (`wsConnected` is false by design after
 * the W3-12 cutover), so the panel offered nothing that could be pressed.
 *
 * The gateway now separates the four situations that null was hiding, and each
 * one gets the control that resolves IT — see the banner below.
 */
type ConductorState = 'ready' | 'not_provisioned' | 'not_account_owner' | 'unavailable';

interface CeoStatus {
  apiAvailable: boolean;
  busy?: boolean;
  degradedMessage?: string | null;
  mode?: 'per_user_card';
  cardHref?: string | null;
  conductorState?: ConductorState;
  boardId?: string | null;
  cardId?: string | null;
}

/** Mirrors chaos.conductor_messages.status (CHECK-constrained to these four). */
type TurnStatus = 'ok' | 'streaming' | 'error' | 'timeout';

type Turn =
  | { role: 'user'; content: string; ts: number }
  | {
      role: 'assistant';
      content: string;
      ts: number;
      toolCalls?: string[];
      /** Carried by /api/ceo/conversation since 2026-07-26. The terminal note is
       *  no longer baked into `content` — it renders as a muted system line
       *  BELOW the bubble so a markdown renderer cannot turn "(This reply ended
       *  in an error.)" into a paragraph in the Conductor's own voice. */
      status?: TurnStatus;
      /** Cards this turn actually created, read from chaos.cards — not scraped
       *  out of the prose, so a receipt survives the socket dying. */
      artifacts?: ConductorArtifact[];
    };

/** The muted system line for a turn that did not finish. Same copy the gateway's
 *  decorateTerminalContent() uses — it just no longer lives inside the prose. */
function terminalNote(status: TurnStatus | undefined): string | null {
  if (status === 'timeout') return 'The Conductor stopped responding.';
  if (status === 'error') return 'This reply ended in an error.';
  return null;
}

const STORAGE_PREFIX = 'chaos.ceo-chat.';

function loadHistory(userId: string): Turn[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-40) as Turn[];
  } catch {
    return [];
  }
}

function saveHistory(userId: string, turns: Turn[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(turns.slice(-40)));
  } catch {
    /* ignore */
  }
}

export interface CeoChatProps {
  /** Founder user id — used to scope localStorage and the WS channel. */
  userId: string;
  /**
   * 2026-06-11: the CEO entry moved out of the (now desktop-hidden)
   * ScopeBar into the bottom of the left ActivityBar rail. The rail
   * wants a square 8×8 button matching the contour icons rather than
   * the top-bar 7×7 chrome, so the trigger styling is overridable.
   * Omitted → original top-bar styling (still used on the mobile bar).
   */
  triggerClassName?: string;
  /** Icon-size override paired with `triggerClassName`. */
  triggerIconClassName?: string;
}

interface CurrentReply {
  content: string;
  toolCalls: string[];
  ts: number;
}

interface CeoSnapshot {
  id: string;
  createdAt: string;
  turnCount: number;
  firstUserPreview: string | null;
  lastAgentPreview: string | null;
}

export function CeoChat({
  userId,
  triggerClassName,
  triggerIconClassName,
}: CeoChatProps): React.ReactElement {
  // Enter-to-send is desktop-only. On mobile (touch keyboards) Enter must
  // insert a newline; the founder sends via the send button instead.
  // (Founder bug report 2026-07-05 — testing the Conductor on his phone.)
  // NB: viewport-width based (<768px), so a very narrow desktop window also
  // counts as mobile and will not send on Enter.
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const [confirmingReset, setConfirmingReset] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [snapshots, setSnapshots] = React.useState<CeoSnapshot[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = React.useState(false);
  const [snapshotsError, setSnapshotsError] = React.useState<string | null>(null);
  const [restoring, setRestoring] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<CeoStatus | null>(null);
  const [turns, setTurns] = React.useState<Turn[]>(() => loadHistory(userId));
  const [draft, setDraft] = React.useState('');
  const [streaming, setStreaming] = React.useState(false);
  const [wsConnected, setWsConnected] = React.useState(false);
  const wsRef = React.useRef<WebSocket | null>(null);
  const currentReply = React.useRef<CurrentReply | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  // `open` as a ref so the socket's own callbacks (which are installed once per
  // connection, not per render) can decide whether a finished turn should also
  // tear the now-unwatched socket down.
  const openRef = React.useRef(open);
  openRef.current = open;

  // ── Live activity (complaint 1) ──────────────────────────────────────
  // Every field below comes from an event that ALREADY arrives on the wire and
  // was being discarded by a `default: return`. No new wire event, no new
  // server work.
  const [reasoningTail, setReasoningTail] = React.useState<string | null>(null);
  const [toolCount, setToolCount] = React.useState(0);
  const [lastTool, setLastTool] = React.useState<string | null>(null);
  // `tool_result` was explicitly dropped (`case 'tool_result': return;`). It is
  // the only signal that distinguishes "the Conductor is grinding" from "the
  // Conductor is grinding and things are failing", so it is counted now.
  const [failedTools, setFailedTools] = React.useState(0);
  const [doneAt, setDoneAt] = React.useState<{ elapsedMs: number; tools: number } | null>(null);

  // ── Voice dictation (spec 05 §1.5 / §4.1 / §4.4) ───────────────────
  // MediaRecorder → POST /api/ceo/voice → fill the draft with the transcript
  // (founder edits before send). Reuses the gateway STT chain
  // (Deepgram→whisper). The server does not persist the audio — which is
  // exactly why the BROWSER must hold it until a transcript comes back
  // (2026-07-20 upload hardening): a dictation exists nowhere else.
  //
  // AND IT HOLDS IT PAST THAT, until the server says the TEXT is saved
  // (2026-08-16). "A transcript came back" was never the same claim as "the
  // meaning is safe": the transcript's only home was the `draft` state below,
  // which is not persisted anywhere, so releasing the audio on it destroyed
  // one copy of the meaning and left the other one reload away from gone. The
  // release now waits for `textDurable` — see the transcribe callback.
  const [recording, setRecording] = React.useState(false);
  // Set when an upload failed: keeps the blob in memory AND names the local
  // stash, so the founder gets a Retry instead of a dead end.
  const [pendingVoice, setPendingVoice] = React.useState<
    { blob: Blob; stashId: string | null; cached: boolean } | null
  >(null);
  const [transcribing, setTranscribing] = React.useState(false);
  const [micDenied, setMicDenied] = React.useState(false);
  const [voiceError, setVoiceError] = React.useState<string | null>(null);
  // A refusal that a retry cannot change — 413 too large, 415 wrong type, or
  // `feature_disabled` (CONDUCTOR_FILL_ENABLED is off, which arrives as a 503).
  // Offering Retry for those was the 2026-08-15 shape in miniature: it invites
  // the person to bury an honest answer under identical failures.
  const [voiceRetryable, setVoiceRetryable] = React.useState(true);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const audioChunksRef = React.useRef<Blob[]>([]);
  const mediaStreamRef = React.useRef<MediaStream | null>(null);

  // Persist turns on every change.
  React.useEffect(() => {
    saveHistory(userId, turns);
  }, [userId, turns]);

  // Fetch /api/ceo/status once on mount, and again every time the sheet opens.
  const refreshStatus = React.useCallback(async () => {
    try {
      const resp = await fetch('/api/ceo/status', { credentials: 'include' });
      if (!resp.ok) {
        setStatus({ apiAvailable: false, degradedMessage: `Status check failed (HTTP ${resp.status})` });
        return;
      }
      setStatus(await resp.json());
    } catch (err: any) {
      setStatus({ apiAvailable: false, degradedMessage: err?.message ?? 'Status check failed' });
    }
  }, []);

  React.useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  // ── Setting the Conductor up, from the panel that says it is missing ──
  //
  // POST /api/conductor/ensure is IDEMPOTENT — the creation path inside it is
  // fenced by an advisory lock in chaos.provision_account_conductor and its
  // race loser returns the winner's Conductor as success — so pressing this
  // twice, or pressing it in two tabs, yields ONE Conductor and one card.
  // That is why the button does not need to guard itself beyond the spinner.
  //
  // It is shown ONLY for `not_provisioned`. In `unavailable` we do not know
  // whether a Conductor exists, and offering to build one there is how a
  // transient lookup failure turns into a second always-on card.
  const [settingUp, setSettingUp] = React.useState(false);
  const [setupError, setSetupError] = React.useState<string | null>(null);
  const setUpConductor = React.useCallback(async () => {
    setSettingUp(true);
    setSetupError(null);
    try {
      const resp = await fetch('/api/conductor/ensure', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      const body = (await resp.json().catch(() => null)) as
        | { cardHref?: string; boardId?: string; cardId?: string; message?: string; error?: string }
        | null;
      if (!resp.ok) {
        // The gateway's sentence when it wrote one (it does for the states a
        // person can act on), and never a bare status code on its own.
        setSetupError(body?.message ?? `Conductor setup failed (HTTP ${resp.status}).`);
        return;
      }
      // Re-read rather than trust this response alone: the same endpoint feeds
      // the banner, so one refresh moves the whole panel to its `ready` shape.
      await refreshStatus();
    } catch (err: any) {
      setSetupError(err?.message ?? 'Conductor setup failed.');
    } finally {
      setSettingUp(false);
    }
  }, [refreshStatus]);

  // ── Cross-device: load the LIVE conversation from the SERVER ─────────
  // The Conductor conversation is durable server-side (chaos.conductor_messages,
  // account-scoped). The server is the source of truth: on open we replace the
  // local turns with the server's, so a second device (empty localStorage) shows
  // the SAME conversation. localStorage remains only an optimistic cache for
  // instant first paint. We skip the replace while a turn is streaming so we
  // never clobber a reply mid-flight.
  const loadServerConversation = React.useCallback(async () => {
    try {
      const resp = await fetch('/api/ceo/conversation', { credentials: 'include' });
      if (!resp.ok) return;
      const body = (await resp.json()) as { turns?: unknown };
      if (!Array.isArray(body.turns)) return;
      const serverTurns: Turn[] = (body.turns as any[])
        .filter((t) => t && (t.role === 'user' || t.role === 'assistant'))
        .map((t, i) => {
          const ts = typeof t.ts === 'number' ? t.ts : Date.now() + i;
          if (t.role === 'user') {
            return { role: 'user', content: String(t.content ?? ''), ts };
          }
          const status =
            t.status === 'ok' || t.status === 'streaming' || t.status === 'error' || t.status === 'timeout'
              ? (t.status as TurnStatus)
              : undefined;
          return {
            role: 'assistant',
            content: String(t.content ?? ''),
            ts,
            toolCalls: Array.isArray(t.toolCalls) ? (t.toolCalls as string[]) : undefined,
            status,
            artifacts: Array.isArray(t.artifacts)
              ? (t.artifacts as ConductorArtifact[]).filter((a) => a && a.cardId && a.boardId)
              : undefined,
          };
        });
      // Server wins — but don't stomp an in-flight stream.
      if (currentReply.current) return;
      setTurns(serverTurns);
    } catch {
      // Offline / transient — keep the optimistic localStorage view.
    }
  }, []);

  // Open/close WebSocket when the sheet opens/closes.
  React.useEffect(() => {
    if (!open) {
      // ⚠ 2026-07-26 — THE DRAWER CLOSING MUST NOT KILL THE TURN.
      //
      // This cleanup used to close the socket unconditionally. Closing it makes
      // the gateway bridge see `downstream close mid-turn` → finalizeTurn('error')
      // → the durable row freezes at whatever preamble had streamed and gets
      // stamped 'error', and one Inbox line fires. Meanwhile the orchestrator
      // (which is deliberately never aborted) runs on for another nine minutes.
      // That is the whole 02:57 loss, and the founder triggers it by doing the
      // most natural thing in the world: closing a drawer while he waits.
      //
      // <CeoChat> is mounted in the app layout and never unmounts, so simply
      // NOT closing while a turn is in flight keeps the socket — and the durable
      // write path — alive across the close. Reopening is then instant, and the
      // e6bbe78 heal path stops being needed on healthy turns (it stays intact
      // and still covers a real tab close / network drop).
      if (!currentReply.current) {
        try {
          wsRef.current?.close();
        } catch {}
        wsRef.current = null;
        setWsConnected(false);
      }
      return;
    }
    void refreshStatus();
    // Warm the markdown chunk while he types rather than at first paint of the
    // first reply. Shares the chunk with the session pane, so a tab that has
    // opened either surface already has it.
    void import('../agent/markdown-content');
    // Pull the durable server-side conversation so every device converges on
    // the same chat (server wins over this device's localStorage).
    void loadServerConversation();
    // The retired bridge is opt-in only for an explicitly resolved legacy
    // status. A fast drawer-open used to race the status read and interpret
    // `null` (or a failed read) as permission to probe /ws/ceo/chat.
    if (!status || status.mode === 'per_user_card' || status.apiAvailable !== true) {
      try { wsRef.current?.close(); } catch {}
      wsRef.current = null;
      setWsConnected(false);
      return;
    }
    // A live socket survived a previous close — reuse it rather than opening a
    // second one against the same orchestrator channel.
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      setWsConnected(true);
      return;
    }
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/ws/ceo/chat`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => {
      setWsConnected(false);
      setStreaming(false);
    };
    ws.onerror = () => {
      setStreaming(false);
    };
    ws.onmessage = (evt) => {
      let parsed: any;
      try {
        parsed = JSON.parse(typeof evt.data === 'string' ? evt.data : '');
      } catch {
        return;
      }
      handleStreamEvent(parsed);
    };
    // NB no teardown here: the `!open` branch at the top of this effect owns
    // the close decision, and it defers while a turn is in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status?.apiAvailable, status?.mode]);

  // ── The truthful "is it working right now" signal ────────────────────
  //
  // /api/ceo/status proxies the orchestrator's busyFlag. It has been fetched on
  // every open since the drawer shipped and `CeoStatus.busy` was parsed and then
  // never read by anything. Reading it is what makes a reload or a second device
  // show an honest state — the client-side `streaming` boolean is per-socket and
  // cannot know about a turn that started before this tab existed.
  //
  // (It only became worth reading on 2026-07-26: founder-chat.ts onClose used to
  // clear the flag on socket close, one line under a comment saying the run
  // continues, so `busy` answered false for the remaining nine minutes.)
  React.useEffect(() => {
    if (!open) return;
    const t = window.setInterval(() => {
      void refreshStatus();
    }, 5000);
    return () => window.clearInterval(t);
  }, [open, refreshStatus]);

  // On the busy true→false edge the turn just finished somewhere we were not
  // watching. One reload swaps the finished answer (and its receipts) in, via
  // the SAME endpoint that runs the heal path — no recovery logic is duplicated.
  const prevBusy = React.useRef<boolean | undefined>(undefined);
  React.useEffect(() => {
    const busy = status?.busy;
    if (prevBusy.current === true && busy === false && !currentReply.current) {
      void loadServerConversation();
    }
    prevBusy.current = busy;
  }, [status?.busy, loadServerConversation]);

  const handleStreamEvent = (evt: any): void => {
    switch (evt?.type) {
      case 'bridge_ready':
      case 'ready':
        return;
      case 'text': {
        if (!currentReply.current) currentReply.current = { content: '', toolCalls: [], ts: Date.now() };
        const cur = currentReply.current;
        cur.content += String(evt.delta ?? '');
        setTurns((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && last.ts === cur.ts) {
            next[next.length - 1] = {
              ...last,
              content: cur.content,
              toolCalls: cur.toolCalls,
            };
          }
          return next;
        });
        scrollToBottom();
        return;
      }
      case 'thinking': {
        // Was dropped by `default: return`. Never appended to turn.content and
        // never persisted — it is a liveness signal, not part of the answer.
        const d = String(evt.delta ?? '').trim();
        if (d) setReasoningTail(d.length > 160 ? `…${d.slice(-160)}` : d);
        return;
      }
      case 'tool_use': {
        if (!currentReply.current) currentReply.current = { content: '', toolCalls: [], ts: Date.now() };
        const cur = currentReply.current;
        cur.toolCalls.push(String(evt.name ?? 'tool'));
        setToolCount((n) => n + 1);
        setLastTool(String(evt.name ?? 'tool'));
        setTurns((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && last.ts === cur.ts) {
            next[next.length - 1] = {
              ...last,
              toolCalls: [...cur.toolCalls],
            };
          }
          return next;
        });
        return;
      }
      case 'tool_result':
        if (evt.ok === false) setFailedTools((n) => n + 1);
        return;
      case 'done': {
        if (currentReply.current) {
          setDoneAt({
            elapsedMs: Math.max(0, Date.now() - currentReply.current.ts),
            tools: currentReply.current.toolCalls.length,
          });
        }
        if (currentReply.current && currentReply.current.content.length === 0) {
          // Empty done — replace with placeholder so the user sees something.
          const ts = currentReply.current.ts;
          setTurns((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant' && last.ts === ts) {
              next[next.length - 1] = { ...last, content: '(no text reply)' };
            }
            return next;
          });
        }
        currentReply.current = null;
        setStreaming(false);
        setReasoningTail(null);
        // The cards this turn created do not exist until now, and the footer
        // reads them from chaos.cards server-side. Same call the reload path
        // uses, so there is exactly one way receipts arrive.
        void loadServerConversation();
        // The drawer was closed while this finished (that is now allowed, and
        // is the point). Nobody is watching the socket — let it go.
        if (!openRef.current) {
          try {
            wsRef.current?.close();
          } catch {}
          wsRef.current = null;
          setWsConnected(false);
        }
        return;
      }
      case 'error':
      case 'bridge_closed':
        if (currentReply.current) {
          const cur = currentReply.current;
          // `\n\n**Error:**` — the bubble renders markdown now, and one newline
          // is not a paragraph break, so the old form glued the failure onto the
          // Conductor's last sentence. Matches ceo-chat-ws.ts byte for byte.
          const msg = `**Error:** ${evt.message ?? evt.reason ?? 'unknown'}`;
          cur.content = (cur.content || '') + (cur.content ? '\n\n' : '') + msg;
          const ts = cur.ts;
          setTurns((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant' && last.ts === ts) {
              next[next.length - 1] = { ...last, content: cur.content };
            }
            return next;
          });
        }
        currentReply.current = null;
        setStreaming(false);
        setReasoningTail(null);
        return;
      default:
        return;
    }
  };

  const scrollToBottom = (): void => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [turns]);

  const submit = (): void => {
    const text = draft.trim();
    if (!text || streaming) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    const userTurn: Turn = { role: 'user', content: text, ts: Date.now() };
    const assistantTurn: Turn = { role: 'assistant', content: '', ts: Date.now() + 1, toolCalls: [] };
    currentReply.current = { content: '', toolCalls: [], ts: assistantTurn.ts };
    setTurns((prev) => [...prev, userTurn, assistantTurn]);
    setDraft('');
    setStreaming(true);
    // Fresh turn → fresh activity. The dock must never show the previous turn's
    // clock or tool count.
    setReasoningTail(null);
    setToolCount(0);
    setLastTool(null);
    setFailedTools(0);
    setDoneAt(null);
    try {
      // 2026-06-13 (Conductor): only the message is sent. The orchestrator
      // is hardwired to the best model + highest reasoning (xhigh) in the
      // backend, so there is no per-turn model/effort to forward.
      wsRef.current.send(JSON.stringify({ message: text }));
    } catch {
      setStreaming(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Desktop: Enter sends, Shift+Enter = newline.
    // Mobile: Enter always inserts a newline (no preventDefault / submit);
    // sending happens only via the send button.
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      submit();
    }
  };

  // ── Voice: start / stop / transcribe ───────────────────────────────
  const stopTracks = (): void => {
    try {
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {}
    mediaStreamRef.current = null;
  };

  const transcribe = React.useCallback(
    async (blob: Blob, existingStashId?: string | null): Promise<void> => {
      setTranscribing(true);
      setVoiceError(null);
      setVoiceRetryable(true);

      // A recording of zero bytes is not a recording. `apiUpload` deliberately
      // lets an empty body through — a zero-byte FILE is a real thing someone
      // drags into the Explorer — so the route for which empty is meaningless
      // says so itself, before anything is stashed or sent.
      if (blob.size === 0) {
        setVoiceError('Nothing was recorded — the microphone produced no audio. Try again.');
        setVoiceRetryable(false);
        setTranscribing(false);
        return;
      }

      // Stash BEFORE the upload. `null` means this browser has no IndexedDB
      // (private window / hardened profile) — we say so rather than implying a
      // safety net that is not there.
      let stashId: string | null = existingStashId ?? null;
      if (stashId === null) {
        try {
          stashId = await voicePersist.stash(blob, {
            cardId: null,
            mime: blob.type || 'audio/webm',
            mode: 'paste',
          });
        } catch {
          stashId = null;
        }
      }
      const cached = stashId !== null;

      try {
        // The gateway accepts field "file" or "audio"; the recorder mime
        // matches voice-inputs.ts accepted set (webm/opus default).
        const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
        // Retry/backoff on a transient reset, 429 or 5xx — a single dropped
        // socket must not cost a dictation.
        const body = await apiUpload<{
          transcript?: string;
          /** The receipt. TRUE means the words are committed server-side. */
          textDurable?: boolean;
          transcriptId?: string | null;
          durabilityReason?: string;
        }>('/ceo/voice', blob, {
          filename: `conductor-voice.${ext}`,
        });
        const text = (body.transcript ?? '').trim();
        if (!text) {
          setVoiceError(
            'No speech detected. The recording is still saved in this browser — ' +
              'retry, or open the Voice tab to download it.',
          );
          // KEPT. An empty transcript is a refusal, and it is exactly the case
          // where the person most wants the audio back to check what happened.
          if (stashId) await voicePersist.markError(stashId, 'empty_transcript').catch(() => undefined);
          setPendingVoice({ blob, stashId, cached });
          return;
        }

        // ── THE RELEASE, AND THE CONDITION ON IT ─────────────────────────
        //
        // This used to be `if (stashId) await voicePersist.drop(stashId)` with
        // the comment "Transcript in hand: the audio has served its purpose."
        // It had not. The transcript's only home was `setDraft` below — a
        // React state variable, in this tab, persisted nowhere — so deleting
        // the recording there destroyed the audio AND left the text one reload
        // away from destruction. voice-persist.ts's own contract already said
        // the rule this violated: "drop(id) → remove ONLY after the server
        // confirms a durable upload".
        //
        // `textDurable` is the server saying a committed row holds these
        // words. It is the only thing that licenses the delete. An older
        // gateway does not send the field, so `=== true` (not truthy-ish) is
        // deliberate: unknown means keep.
        if (body.textDurable === true) {
          if (stashId) await voicePersist.drop(stashId).catch(() => undefined);
          setPendingVoice(null);
        } else {
          // The words came back but nothing saved them. Keep the recording,
          // and say so — this is a state the founder can act on (send the turn
          // now, or Download), not an error to hide.
          if (stashId) {
            await voicePersist
              .markError(stashId, `text_not_durable:${body.durabilityReason ?? 'unknown'}`)
              .catch(() => undefined);
          }
          setPendingVoice({ blob, stashId, cached });
          setVoiceError(
            cached
              ? 'Transcribed, but the text could not be saved on the server, so the recording ' +
                'is being kept in this browser. Send the message to keep the words, or use Download.'
              : 'Transcribed, but the text could not be saved on the server AND this browser ' +
                'cannot cache audio — the recording only lives in this tab. Send the message ' +
                'before closing it.',
          );
        }
        // Append to the draft so the founder can edit before sending.
        setDraft((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
      } catch (err: unknown) {
        const apiErr = err instanceof ApiError ? err : null;
        // A refusal that will never succeed on a retry keeps its own message
        // and takes the Retry button away, rather than letting the person
        // replace an honest answer with three identical ones.
        const permanent = isPermanentUploadRefusal(err);
        setVoiceRetryable(!permanent);
        // Only claim "the feature is off" when the gateway actually said so.
        // A 503 with any other code is an overloaded service, and telling the
        // founder to go flip a flag would send him after the wrong thing.
        if (apiErr?.status === 503 && apiErr.code === 'feature_disabled') {
          setVoiceError(
            'Voice is off — enable the Conductor feature on the server. ' +
              (cached
                ? 'The recording is saved in this browser; open the Voice tab to download it.'
                : 'This browser cannot cache audio, so download the recording before closing this tab.'),
          );
        } else {
          const detail = apiErr?.message ?? (err as Error)?.message ?? 'Transcription failed';
          const wayOut = permanent
            ? cached
              ? 'Retrying will not change this. The recording is saved in this browser — open the Voice tab to download it.'
              : 'Retrying will not change this, and this browser cannot cache audio — download the recording before closing this tab.'
            : cached
              ? 'The recording is saved in this browser — press Retry.'
              : 'This browser cannot cache audio, so the recording only lives in this tab — press Retry before closing it.';
          setVoiceError(`${detail}. ${wayOut}`);
        }
        if (stashId) {
          const reason = apiErr?.message ?? (err as Error)?.message ?? 'upload_failed';
          await voicePersist.markError(stashId, reason).catch(() => undefined);
        }
        // Keep the bytes reachable so the founder has a way forward.
        setPendingVoice({ blob, stashId, cached });
      } finally {
        setTranscribing(false);
      }
    },
    [],
  );

  const retryPendingVoice = React.useCallback((): void => {
    if (!pendingVoice || transcribing || !voiceRetryable) return;
    void transcribe(pendingVoice.blob, pendingVoice.stashId);
  }, [pendingVoice, transcribing, voiceRetryable, transcribe]);

  const discardPendingVoice = React.useCallback((): void => {
    if (!pendingVoice) return;
    if (pendingVoice.stashId) void voicePersist.drop(pendingVoice.stashId).catch(() => undefined);
    setPendingVoice(null);
    setVoiceError(null);
    setVoiceRetryable(true);
  }, [pendingVoice]);

  const startRecording = React.useCallback(async (): Promise<void> => {
    setVoiceError(null);
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setVoiceError('Voice recording is not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      setMicDenied(false);
      // Prefer opus/webm (Deepgram + whisper both accept it; matches the
      // per-card voice-inputs.ts accepted types).
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stopTracks();
        setRecording(false);
        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        if (chunks.length === 0) return;
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        void transcribe(blob);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      // Warm-up gate (founder 2026-07-12): show the red "recording" state only
      // after the mic pipeline is warm, so the first words aren't clipped on
      // Safari/iOS. The recorder is already capturing the harmless leading
      // silence during this window.
      const START_WARMUP_MS = 500;
      window.setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') setRecording(true);
      }, START_WARMUP_MS);
    } catch (err: any) {
      stopTracks();
      // NotAllowedError / SecurityError → mic permission denied.
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        setMicDenied(true);
      } else {
        setVoiceError(err?.message ?? 'Could not access the microphone.');
      }
    }
  }, [transcribe]);

  const stopRecording = React.useCallback((): void => {
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      stopTracks();
      setRecording(false);
    }
  }, []);

  const toggleRecording = (): void => {
    if (recording) stopRecording();
    else void startRecording();
  };

  // Stop any in-flight recording when the drawer closes (free the mic).
  React.useEffect(() => {
    if (!open && recording) stopRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Release the mic on unmount.
  React.useEffect(() => stopTracks, []);

  const reset = async (): Promise<void> => {
    // M-X41 (Wave-2): persist the current conversation as a snapshot
    // BEFORE clearing it. The Reset is no longer destructive — the
    // founder can restore from History at any time.
    const turnsToSave = turns;
    setConfirmingReset(false);
    if (turnsToSave.length === 0) {
      setTurns([]);
      return;
    }
    try {
      await fetch('/api/ceo/reset', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          turns: turnsToSave.map((t) => ({
            role: t.role,
            content: t.content,
            ts: t.ts,
            // toolCalls is optional metadata; persist when present so
            // restored conversations keep their tool annotations.
            ...(t.role === 'assistant' && t.toolCalls
              ? { toolCalls: t.toolCalls }
              : {}),
          })),
        }),
      });
    } catch {
      // Network failure shouldn't block the reset itself — but warn.
      console.warn('[ceo-chat] Snapshot save failed; clearing anyway.');
    }
    setTurns([]);
  };

  const loadSnapshots = React.useCallback(async () => {
    setSnapshotsLoading(true);
    setSnapshotsError(null);
    try {
      const resp = await fetch('/api/ceo/snapshots', {
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const body = (await resp.json()) as { snapshots?: CeoSnapshot[] };
      setSnapshots(Array.isArray(body.snapshots) ? body.snapshots : []);
    } catch (err: any) {
      setSnapshotsError(err?.message ?? 'Failed to load history');
    } finally {
      setSnapshotsLoading(false);
    }
  }, []);

  // Auto-load when the History modal opens.
  React.useEffect(() => {
    if (historyOpen) void loadSnapshots();
  }, [historyOpen, loadSnapshots]);

  const restoreSnapshot = async (id: string): Promise<void> => {
    setRestoring(id);
    try {
      const resp = await fetch(
        `/api/ceo/snapshots/${encodeURIComponent(id)}/restore`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            // Roll the currently visible turns into a fresh snapshot so
            // the restore is itself undoable.
            currentTurns: turns.map((t) => ({
              role: t.role,
              content: t.content,
              ts: t.ts,
              ...(t.role === 'assistant' && t.toolCalls
                ? { toolCalls: t.toolCalls }
                : {}),
            })),
          }),
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const body = (await resp.json()) as { turns?: unknown };
      if (Array.isArray(body.turns)) {
        const restored: Turn[] = (body.turns as any[])
          .filter((t) => t && (t.role === 'user' || t.role === 'assistant'))
          .map((t, i) => {
            if (t.role === 'user') {
              return {
                role: 'user',
                content: String(t.content ?? ''),
                ts: typeof t.ts === 'number' ? t.ts : Date.now() + i,
              };
            }
            return {
              role: 'assistant',
              content: String(t.content ?? ''),
              ts: typeof t.ts === 'number' ? t.ts : Date.now() + i,
              toolCalls: Array.isArray(t.toolCalls)
                ? (t.toolCalls as string[])
                : undefined,
            };
          });
        setTurns(restored);
        setHistoryOpen(false);
      }
    } catch (err: any) {
      setSnapshotsError(`Restore failed: ${err?.message ?? 'unknown'}`);
    } finally {
      setRestoring(null);
    }
  };

  const statusColor = status
    ? status.apiAvailable
      ? 'text-emerald-500'
      : 'text-amber-500'
    : 'text-muted-foreground';
  const perUserReady =
    status?.mode === 'per_user_card' &&
    status.conductorState === 'ready' &&
    typeof status.cardId === 'string' &&
    status.cardId.length > 0;

  // ── Activity dock view-model ─────────────────────────────────────────
  //
  // ⚠ GATED ON `streaming || status.busy`, NEVER ON CONTENT EMPTINESS. That is
  // the crux of complaint 1. Once the weld fix landed, the Conductor's first
  // preamble arrives within seconds as a clean, finished-LOOKING paragraph —
  // which is precisely the illusion that made the founder close the drawer at
  // 30s on a nine-minute turn.
  const newestTurn = turns.length > 0 ? turns[turns.length - 1] : undefined;
  const serverBusy = status?.busy === true;
  const live =
    streaming ||
    // Reload / second device: no socket ever saw this turn start, but the row
    // is there and the orchestrator says it is working. openAssistantTurn
    // inserts that row at SEND time, so its created_at IS the turn start and
    // the clock is right without a byte of new server state.
    (serverBusy && newestTurn?.role === 'assistant');
  const liveStartedAt = currentReply.current?.ts
    ?? (newestTurn?.role === 'assistant' ? newestTurn.ts : Date.now());
  const activityKind: ConductorActivityKind = live
    ? toolCount > 0
      ? 'working'
      : reasoningTail
        ? 'thinking'
        : 'waiting'
    : doneAt
      ? 'done'
      : 'idle';
  const activityLabel =
    activityKind === 'working'
      ? 'Working'
      : activityKind === 'thinking'
        ? 'Thinking'
        : activityKind === 'waiting'
          ? 'Starting'
          : 'Done';

  return (
    <>
      <button
        type="button"
        aria-label="Open Conductor chat"
        title={status?.apiAvailable === false ? (status.degradedMessage ?? 'Conductor offline') : 'Conductor'}
        onClick={() => setOpen(true)}
        className={cn(
          // Mobile (<sm): 44×44 to meet iOS HIG touch-target minimum.
          // Desktop (sm+): keep the compact 28×28 chrome that fits the top bar.
          'relative flex h-11 w-11 sm:h-7 sm:w-7 items-center justify-center rounded transition-colors',
          'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
          // 2026-06-11: when mounted in the ActivityBar rail, the caller
          // passes a square 8×8 class so the CEO entry matches the
          // contour buttons. The override wins over the defaults above.
          triggerClassName,
        )}
      >
        <Bot className={cn('h-5 w-5 sm:h-4 sm:w-4', triggerIconClassName, statusColor)} aria-hidden />
        {status && !status.apiAvailable && (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-500 ring-1 ring-background"
          />
        )}
        {/* The literal "close this and come back later" affordance: while the
            drawer is shut and a turn is still running, the launcher itself
            pulses. Without it, closing the drawer looks exactly like nothing is
            happening — which is what taught the founder to distrust it. */}
        {!open && live && status?.apiAvailable !== false && (
          <span
            aria-hidden
            data-testid="conductor-launcher-busy"
            className="absolute -right-0.5 -top-0.5 flex h-2 w-2"
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-background" />
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        {/* hideCloseIcon: the built-in Sheet X is a 16×16 tap target with a
            focus-ring transition that swallows the first tap on mobile (founder
            reported needing 2-3 taps). We render our own 44×44 SheetClose
            below with touch-action:manipulation + onPointerDown to fire on
            the very first tap. */}
        <SheetContent
          hideCloseIcon
          side="right"
          className="flex h-full flex-col p-0 sm:w-[480px]"
        >
          <SheetHeader className="flex flex-row items-center gap-3 border-b border-border px-3 py-3 sm:px-4">
            {/* LEFT cluster: Reset button (away from X), then title block. */}
            {!perUserReady && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirmingReset(true)}
                disabled={streaming || turns.length === 0}
                className="shrink-0 touch-manipulation"
                aria-label="Reset conversation"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                Reset
              </Button>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <SheetTitle className="flex items-center gap-1.5 truncate text-sm font-medium">
                <Bot className={cn('h-4 w-4 shrink-0', statusColor)} aria-hidden />
                <span className="truncate">Conductor</span>
                {/* M-X41 (Wave-2): 3-dot menu right after the title — small
                    enough to live inline with the text but still a 44×44
                    tap target on mobile (sm:32). First menu item is
                    "History" — opens the past-conversations modal. */}
                {!perUserReady && <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Conductor chat menu"
                      className={cn(
                        'shrink-0 inline-flex items-center justify-center rounded-md',
                        'h-11 w-11 sm:h-7 sm:w-7',
                        'text-muted-foreground hover:bg-muted hover:text-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        'touch-manipulation',
                      )}
                      data-testid="ceo-chat-menu"
                    >
                      <MoreVertical
                        className="h-4 w-4 sm:h-3.5 sm:w-3.5"
                        aria-hidden
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[14rem]">
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        setHistoryOpen(true);
                      }}
                      data-testid="ceo-chat-menu-history"
                    >
                      <HistoryIcon className="mr-2 h-3.5 w-3.5" aria-hidden />
                      History
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>}
              </SheetTitle>
              {/* 2026-06-13 (Conductor): the model/effort pickers are gone —
                  the orchestrator is hardwired to best model + highest
                  reasoning, so the status line just reflects "Max". */}
              <SheetDescription className="truncate text-xs text-muted-foreground">
                {status?.apiAvailable ? (
                  <>
                    <span>Online</span>
                    <span className="hidden sm:inline"> · cross-Workspace</span>
                    <span>{' · Max'}</span>
                  </>
                ) : (
                  status?.degradedMessage ?? 'Checking status…'
                )}
              </SheetDescription>
            </div>
            {/* RIGHT: custom close button. Why we don't use the default Sheet X:
                  - 16×16 tap target violates iOS HIG 44pt minimum.
                  - focus-ring transition fires on first tap, so the second tap
                    is what actually triggers click → "double-tap to close" bug.
                Our version:
                  - 44×44 mobile tap target (sm:36×36 desktop).
                  - touch-action:manipulation removes the 300ms tap delay
                    Mobile Safari adds for double-tap-zoom detection.
                  - SheetClose (DialogPrimitive.Close) handles dismiss
                    on the first synthetic click event from Radix; no
                    focus-ring race. */}
            <SheetClose asChild>
              <button
                type="button"
                aria-label="Close Conductor chat"
                className={cn(
                  'shrink-0 inline-flex items-center justify-center rounded-md',
                  'h-11 w-11 sm:h-9 sm:w-9',
                  'text-muted-foreground hover:bg-muted hover:text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'touch-manipulation',
                )}
              >
                <X className="h-5 w-5 sm:h-4 sm:w-4" aria-hidden />
              </button>
            </SheetClose>
          </SheetHeader>

          {perUserReady ? (
            <div className="flex min-h-0 flex-1 flex-col px-3 py-2">
              <div className="min-h-0 flex-1">
                <React.Suspense
                  fallback={(
                    <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Loading Conductor…
                    </div>
                  )}
                >
                  <ConductorSessionChat cardId={status.cardId!} />
                </React.Suspense>
              </div>
            </div>
          ) : (
          <>
          <ScrollArea className="flex-1">
            <div ref={scrollRef} className="flex flex-col gap-3 px-4 py-3">
              {turns.length === 0 && (
                <div className="rounded border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                  Ask your Conductor anything about your workspaces. It can list
                  cards, query roles, spawn work, escalate. Try “What’s in Backlog?”
                  or “Who handles Treasury questions?”
                </div>
              )}
              {turns.map((turn, i) => (
                <TurnRow
                  key={turn.ts}
                  turn={turn}
                  showReceipts
                  streaming={live && i === turns.length - 1 && turn.role === 'assistant'}
                />
              ))}
            </div>
          </ScrollArea>

          {/* The activity strip — the structural slot SessionActivityDock
              occupies in the session pane: a shrink-0 band between the scroller
              and the composer, so it is pinned above the fold no matter how
              long the conversation is. */}
          {env.VITE_CONDUCTOR_RICH_RENDER && (
            <ConductorActivity
              kind={activityKind}
              label={activityLabel}
              startedAtMs={activityKind === 'done' ? Date.now() - (doneAt?.elapsedMs ?? 0) : liveStartedAt}
              toolCount={activityKind === 'done' ? (doneAt?.tools ?? 0) : toolCount}
              failedTools={failedTools}
              lastTool={lastTool ?? undefined}
              reasoningTail={reasoningTail ?? undefined}
            />
          )}

          <div className="border-t border-border bg-background p-3">
            {!wsConnected && status?.apiAvailable !== false && (
              <div className="mb-2 flex items-center gap-2 rounded border border-border bg-muted/20 px-2 py-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                Connecting…
              </div>
            )}
            {/* ── THE BANNER THAT USED TO BE A DEAD END ──────────────────
                Every arm below now ends in something the founder can press,
                except the one where nothing he can press would help (he does
                not own an account). What shipped before had a single arm: the
                text, plus an "Open card" anchor that rendered ONLY when
                `cardHref` was non-null — and with chaos.account_conductor_state
                empty it never was, so the panel showed a sentence telling him
                to call /api/conductor/provision himself, above a composer that
                is disabled by design. */}
            {status && !status.apiAvailable && (
              <div
                className="mb-2 flex flex-wrap items-center gap-2 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-xs text-amber-600"
                data-testid="conductor-status-banner"
                data-conductor-state={status.conductorState ?? 'legacy'}
              >
                <AlertTriangle className="h-3 w-3" aria-hidden />
                <span className="min-w-0 flex-1">
                  {status.mode === 'per_user_card'
                    ? status.degradedMessage
                    : 'Conductor is offline — check the subscription credentials on the orchestrator container.'}
                </span>
                {/* not_provisioned → the control that was missing. */}
                {status.mode === 'per_user_card' && status.conductorState === 'not_provisioned' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 shrink-0 px-2 text-xs"
                    disabled={settingUp}
                    onClick={() => void setUpConductor()}
                    data-testid="conductor-setup"
                  >
                    {settingUp ? (
                      <>
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
                        Setting up…
                      </>
                    ) : (
                      'Set up Conductor'
                    )}
                  </Button>
                )}
                {/* unavailable → retry, and deliberately NOT "create one": we
                    do not know whether one exists. */}
                {status.mode === 'per_user_card' && status.conductorState === 'unavailable' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 shrink-0 px-2 text-xs"
                    onClick={() => void refreshStatus()}
                    data-testid="conductor-status-retry"
                  >
                    Retry
                  </Button>
                )}
              </div>
            )}
            {setupError && (
              <div
                className="mb-2 flex items-center gap-2 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-xs text-amber-600"
                data-testid="conductor-setup-error"
              >
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1">{setupError}</span>
              </div>
            )}
            {/* Voice states (spec 05 §4.4) — mic-denied empty state + transient
                transcription errors. */}
            {micDenied && (
              <div className="mb-2 flex items-center gap-2 rounded border border-border bg-muted/20 px-2 py-1 text-xs text-muted-foreground">
                <Mic className="h-3 w-3" aria-hidden />
                Microphone blocked — allow mic access in your browser to dictate, or type instead.
              </div>
            )}
            {voiceError && (
              <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-xs text-amber-600">
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1">{voiceError}</span>
                {pendingVoice && (
                  <span className="flex shrink-0 items-center gap-1">
                    {/* No Retry on a refusal a retry cannot change (413, 415,
                        the feature flag being off). The message above already
                        says what to do instead — keep the audio. */}
                    {voiceRetryable && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-xs"
                        disabled={transcribing}
                        onClick={retryPendingVoice}
                      >
                        Retry
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      disabled={transcribing}
                      onClick={discardPendingVoice}
                    >
                      Discard
                    </Button>
                  </span>
                )}
              </div>
            )}
            {recording && (
              <div className="mb-2 flex items-center gap-2 rounded border border-border bg-muted/20 px-2 py-1 text-xs text-muted-foreground">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-red-500" aria-hidden />
                Recording… tap the stop button when done.
              </div>
            )}
            {/* A turn started on another device / before this reload. The
                orchestrator now refuses a concurrent turn outright
                (founder-chat.ts), so say so here rather than let him type into a
                composer whose send will bounce. */}
            {!streaming && live && (
              <div className="mb-2 flex items-center gap-2 rounded border border-border bg-muted/20 px-2 py-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                The Conductor is still working on your previous message.
              </div>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Message your Conductor, or tap the mic…"
                disabled={live || !wsConnected || transcribing}
                rows={2}
                className="min-h-[44px] resize-none"
              />
              {/* Mic button (spec 05 §4.1) — 44×44 thumb target on mobile. */}
              <Button
                  type="button"
                  size="icon"
                  variant={recording ? 'destructive' : 'outline'}
                  onClick={toggleRecording}
                  disabled={live || !wsConnected || transcribing}
                  aria-label={recording ? 'Stop recording' : 'Record voice message'}
                  title={recording ? 'Stop recording' : 'Record voice message'}
                  className="h-11 w-11 shrink-0 touch-manipulation sm:h-10 sm:w-10"
                >
                  {transcribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : recording ? (
                    <Square className="h-4 w-4" aria-hidden />
                  ) : (
                    <Mic className="h-4 w-4" aria-hidden />
                  )}
              </Button>
              <Button
                type="button"
                size="icon"
                onClick={submit}
                disabled={live || !wsConnected || !draft.trim()}
                aria-label="Send"
                className="h-11 w-11 shrink-0 sm:h-10 sm:w-10"
              >
                {live ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-4 w-4" aria-hidden />
                )}
              </Button>
            </div>
          </div>
          </>
          )}
        </SheetContent>
      </Sheet>

      {/* Reset confirmation. Lives outside <Sheet> so the dialog overlay sits
          above the slide-over and Escape/outside-click cancels safely. */}
      <Dialog
        open={confirmingReset}
        onOpenChange={(next) => setConfirmingReset(next)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-muted-foreground" aria-hidden />
              <DialogTitle>Reset conversation?</DialogTitle>
            </div>
            <DialogDescription>
              The current conversation will be saved to History and your Conductor
              will start fresh. You can restore any past conversation from the
              History menu.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmingReset(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                void reset();
              }}
              autoFocus
            >
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History modal — past CEO conversations. Lives outside the
          drawer so its overlay can sit on top, and Escape/outside-click
          dismisses cleanly. */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent
          className="max-w-2xl"
          data-testid="ceo-history-dialog"
        >
          <DialogHeader>
            <div className="flex items-center gap-2">
              <HistoryIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
              <DialogTitle>Conductor chat history</DialogTitle>
            </div>
            <DialogDescription>
              Every Reset saves the current conversation here. Click Restore to
              load any past conversation back into the chat. The conversation
              you have open right now is auto-saved before restore.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[24rem]">
            <div className="flex flex-col gap-2">
              {snapshotsLoading && (
                <div className="flex items-center gap-2 rounded border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  Loading history…
                </div>
              )}
              {snapshotsError && !snapshotsLoading && (
                <div className="flex items-center gap-2 rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-600">
                  <AlertTriangle className="h-3 w-3" aria-hidden />
                  {snapshotsError}
                </div>
              )}
              {!snapshotsLoading && !snapshotsError && snapshots.length === 0 && (
                <div className="rounded border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                  No past conversations yet. The first Reset will save the
                  current chat here.
                </div>
              )}
              {snapshots.map((snap) => (
                <SnapshotRow
                  key={snap.id}
                  snap={snap}
                  restoring={restoring === snap.id}
                  onRestore={() => {
                    void restoreSnapshot(snap.id);
                  }}
                />
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setHistoryOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SnapshotRow({
  snap,
  restoring,
  onRestore,
}: {
  snap: CeoSnapshot;
  restoring: boolean;
  onRestore: () => void;
}): React.ReactElement {
  const date = new Date(snap.createdAt);
  const dateLabel = date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-border bg-card/40 p-3"
      data-testid="ceo-snapshot-row"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <span className="text-xs font-medium text-foreground">{dateLabel}</span>
          <span className="text-[10px] text-muted-foreground">
            {snap.turnCount} {snap.turnCount === 1 ? 'turn' : 'turns'}
          </span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRestore}
          disabled={restoring}
          data-testid="ceo-snapshot-restore"
        >
          {restoring ? (
            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <Undo2 className="mr-1.5 h-3 w-3" aria-hidden />
          )}
          Restore
        </Button>
      </div>
      {(snap.firstUserPreview || snap.lastAgentPreview) && (
        <div className="flex flex-col gap-1 text-xs">
          {snap.firstUserPreview && (
            <div className="line-clamp-2 text-muted-foreground">
              <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                you ·{' '}
              </span>
              {snap.firstUserPreview}
            </div>
          )}
          {snap.lastAgentPreview && (
            <div className="line-clamp-2 text-muted-foreground">
              <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                conductor ·{' '}
              </span>
              {snap.lastAgentPreview}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One Conductor turn.
 *
 * Exported for tests only — <CeoChat> is a WebSocket + react-query + Radix
 * Sheet assembly, so the row is exercised in isolation. The live path is
 * layout/shell.tsx:348 → layout/activity-bar.tsx:137 → <CeoChat> →
 * ceo-chat.tsx:873 → here (and a second live mount via layout/scope-bar.tsx:224
 * on the board and inbox routes).
 */
function TurnRowImpl({
  turn,
  showReceipts,
  streaming,
}: {
  turn: Turn;
  showReceipts: boolean;
  /** This row is the turn currently in flight — draws the pulsing caret. */
  streaming?: boolean;
}): React.ReactElement {
  const isUser = turn.role === 'user';
  // `warn: false` → returns undefined instead of console-warning when there is
  // no RouterProvider. copy-sites.test.tsx renders <TurnRow> bare, and a hook
  // that throws (or shouts) there would be a test regression caused by a
  // navigation nicety. The handler no-ops without a router; the <Link> chips
  // below are unaffected either way.
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined;

  // ── Internal links must not full-page-reload ────────────────────────
  //
  // THE TRAP, and it is why a naive MarkdownContent drop-in makes his receipt
  // links WORSE than today: the Conductor's own deep link is
  // `/board/<uuid>?card=<uuid>` (conductor.ts:398), and nothing reads `?card=`
  // — board/$boardId.tsx's validateBoardSearch keeps only `view` and the card
  // panel opens solely from the `/card/$cardId` CHILD ROUTE. Rendered as a
  // markdown anchor it is a plain relative <a>: full document reload, dropped
  // query, no card panel, and the drawer's live socket dies with the page.
  //
  // So the anchor is intercepted, normalised through the SAME DEEP_LINK_RE the
  // receipt chips already use, and handed to the router as the path form.
  // Modifier/middle clicks keep native behaviour; external http(s) links keep
  // markdownComponents' target="_blank" and never reach here.
  const onProseClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a');
      const href = a?.getAttribute('href');
      if (!a || !href || !href.startsWith('/') || href.startsWith('//')) return;
      if (a.getAttribute('target') === '_blank') return;
      e.preventDefault();
      if (!router) return;
      DEEP_LINK_RE.lastIndex = 0;
      const m = DEEP_LINK_RE.exec(href);
      DEEP_LINK_RE.lastIndex = 0;
      const boardId = m?.[1];
      const cardId = m?.[2] ?? m?.[3];
      try {
        if (boardId && cardId) {
          void router.navigate({
            to: '/board/$boardId/card/$cardId',
            params: { boardId, cardId },
          });
        } else {
          void router.navigate({ href } as never);
        }
      } catch {
        window.location.assign(href);
      }
    },
    [router],
  );
  // Conductor receipt chips (spec 05 §4.1): recover the new card's deep-link
  // from the assistant prose and render a tappable "Open card" chip
  // (showReceipts). Wraps one-per-line on mobile.
  //
  // 2026-07-26: the prose scrape is now the FALLBACK, not the source. The
  // server reads chaos.cards (created_via='conductor') and hands back real
  // titles, columns, priorities and org names, which survive a dropped socket
  // and a Conductor that forgot to paste the link. Merge both, dedupe by
  // cardId, prefer the server entry — the scrape only ever knew two UUIDs.
  // Flagged: with VITE_CONDUCTOR_RICH_RENDER off the server entries are ignored
  // entirely and the block degrades to today's prose-scraped "Open card" chips
  // with no header — byte-identical to the current build.
  const serverArtifacts =
    env.VITE_CONDUCTOR_RICH_RENDER && showReceipts && turn.role === 'assistant'
      ? (turn.artifacts ?? [])
      : [];
  const links =
    showReceipts && turn.role === 'assistant' ? extractCardLinks(turn.content) : [];
  const receipts: ConductorArtifact[] = React.useMemo(() => {
    const byId = new Map<string, ConductorArtifact>();
    for (const a of serverArtifacts) {
      if (a?.cardId && a?.boardId) byId.set(a.cardId, a);
    }
    for (const lk of links) {
      if (!byId.has(lk.cardId)) byId.set(lk.cardId, { cardId: lk.cardId, boardId: lk.boardId });
    }
    return Array.from(byId.values());
  }, [conductorArtifactsSignature(serverArtifacts), links.map((l) => l.cardId).join(',')]);
  // A Conductor turn is a `whitespace-pre-wrap` block exactly like the agent
  // message that already got a copy button in the session transcript — and it
  // is the one place where there is NO server-side copy to fall back on: these
  // turns are persisted only to localStorage under `chaos.ceo-chat.<userId>`
  // (STORAGE_PREFIX above). Lose the drag-select and the text is gone.
  // CopyButton renders nothing when `turn.content` is empty, so a still-
  // streaming assistant turn (the "…" placeholder) shows no affordance.
  const copy = (
    <CopyButton
      text={turn.content}
      label={isUser ? 'Copy message' : 'Copy reply'}
      // 24px box, ±10px on coarse ⇒ a real 44 × 44. It is laid out on the
      // SAME flex line as the bubble, not under it, so it adds no height to a
      // conversation. Arithmetic:
      //   VERTICAL   `items-end` bottoms the glyph out with the bubble, and
      //              the ::after reaches 10px below the row. The column's
      //              coarse gap is `gap-3` (12px), so it stops 2px short of
      //              the next flex item — which for an assistant turn is the
      //              tool-call chip row or the "Open card" receipt <Link>,
      //              and that Link IS interactive. gap-1 (4px) would have put
      //              the invisible box 6px inside it, and CopyButton is
      //              `relative`, so it would have won the tap.
      //   HORIZONTAL the glyph sits at the column's outer edge and the
      //              ::after reaches 10px further out, into the 16px `px-4`
      //              padding of the scroll container at ceo-chat.tsx:864.
      //              Nothing is clipped: the ScrollArea viewport's clip box is
      //              that div's padding box.
      className="mb-0.5 h-6 w-6 shrink-0 pointer-coarse:after:-inset-x-2.5 pointer-coarse:after:-inset-y-2.5"
      iconClassName="h-3 w-3"
      data-testid="conductor-turn-copy"
    />
  );
  return (
    <div
      className={cn(
        'flex flex-col gap-1 pointer-coarse:gap-3',
        isUser ? 'items-end' : 'items-start',
      )}
    >
      <div
        className={cn(
          'flex w-full items-end gap-1',
          isUser ? 'justify-end' : 'justify-start',
        )}
      >
        {isUser && copy}
        <div
          className={cn(
            'rounded-md px-3 py-2 text-sm',
            // A table or a fenced code block inside an 85%-width rounded pill
            // reads badly, so the assistant bubble takes the full column now
            // that it can contain block-level markdown. The user pill is
            // unchanged — his own messages are short and plain.
            isUser
              ? 'max-w-[85%] whitespace-pre-wrap bg-foreground text-background'
              : 'min-w-0 max-w-full bg-muted/40 text-foreground',
          )}
        >
          {turn.role === 'assistant' ? (
            turn.content ? (
              <div
                onClickCapture={onProseClick}
                // Copied VERBATIM from session-chat-pane.tsx:5307. A hand-tuned
                // variant is precisely how the two surfaces drift, and the
                // founder asked for "the same look we have in a session".
                className="prose prose-sm max-w-none break-words [overflow-wrap:anywhere] dark:prose-invert prose-pre:overflow-x-auto prose-table:block prose-table:max-w-full prose-table:overflow-x-auto"
              >
                <React.Suspense
                  fallback={
                    // The fallback IS today's exact render, so this change is
                    // strictly additive: worst case (chunk still loading, or a
                    // legacy browser) he sees what he sees now, never a blank
                    // or a spinner. It is also why <TurnRow> rendered bare in
                    // copy-sites.test.tsx resolves synchronously instead of
                    // suspending.
                    <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                      {turn.content}
                    </p>
                  }
                >
                  <MarkdownContent>{turn.content}</MarkdownContent>
                </React.Suspense>
                {streaming && (
                  <span
                    aria-hidden
                    className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-foreground/70 align-middle"
                    data-testid="live-typing-cursor"
                  />
                )}
              </div>
            ) : streaming ? (
              <span
                aria-hidden
                className="inline-block h-3.5 w-[2px] animate-pulse bg-foreground/70 align-middle"
                data-testid="live-typing-cursor"
              />
            ) : (
              <em className="opacity-60">…</em>
            )
          ) : (
            turn.content
          )}
        </div>
        {!isUser && copy}
      </div>
      {/* The terminal note, OUTSIDE the prose. The gateway used to append
          "(This reply ended in an error.)" into content; under a markdown
          renderer that becomes a paragraph in the Conductor's own voice. Same
          sentence, same guarantee that a cut-off turn always looks cut off —
          it just no longer impersonates the Conductor. */}
      {turn.role === 'assistant' && terminalNote(turn.status) && (
        <div
          className="flex items-center gap-1.5 px-1 text-[11px] text-muted-foreground"
          data-testid="conductor-terminal-note"
        >
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
          <span>{terminalNote(turn.status)}</span>
        </div>
      )}
      {turn.role === 'assistant' && turn.toolCalls && turn.toolCalls.length > 0 && (
        <div className="flex flex-wrap gap-1 px-1">
          {turn.toolCalls.map((tc, i) => (
            <span
              key={`${turn.ts}-${i}-${tc}`}
              // `title` keeps the raw string — humanizing hides nothing, it just
              // stops a chip reading `/bin/bash -lc "sed -n '1,240p' /tmp/
              // ceo-codex-home-…/SKILL.md"`, which is what seq 34 persisted.
              title={tc}
              className="inline-flex max-w-full items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground"
            >
              <Wrench className="h-2.5 w-2.5 shrink-0" aria-hidden />
              <span className="truncate">{humanizeToolName(tc)}</span>
            </span>
          ))}
        </div>
      )}
      {receipts.length > 0 && <ConductorReceipts turnTs={turn.ts} receipts={receipts} />}
    </div>
  );
}

/**
 * "Created" — the receipts block the founder asked for.
 *
 * Visual language follows ArtifactsFooter in the session pane (small icon +
 * label header, then one row per item) but it is a SEPARATE component on
 * purpose: that footer is fed by a filesystem scan of a card workspace, and the
 * Conductor has no card workspace. Same shape, incompatible source.
 *
 * Label is "Created", not "Artifacts": these are cards, not files.
 *
 * Navigation is a real <Link> to the path route, never a markdown anchor —
 * see the `?card=` trap documented on onProseClick above.
 */
function ConductorReceipts({
  turnTs,
  receipts,
}: {
  turnTs: number;
  receipts: ConductorArtifact[];
}): React.ReactElement {
  return (
    <div
      className="flex w-full flex-col gap-1 px-1"
      data-testid="conductor-artifacts-footer"
    >
      {env.VITE_CONDUCTOR_RICH_RENDER && (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">
          <ListChecks className="h-3 w-3" aria-hidden />
          Created
        </span>
      )}
      {receipts.map((r) => {
        const summary = conductorArtifactSummary(r);
        return (
          <Link
            key={`${turnTs}-${r.boardId}-${r.cardId}`}
            to="/board/$boardId/card/$cardId"
            params={{ boardId: r.boardId, cardId: r.cardId }}
            className={cn(
              'inline-flex min-w-0 max-w-full flex-col gap-0.5 rounded-md border border-border bg-background px-2 py-1',
              'text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
              'touch-manipulation',
            )}
            data-testid="conductor-receipt-link"
          >
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
              {/* A prose-scraped entry has no title — only two UUIDs — so it
                  keeps today's wording rather than inventing one. */}
              <span className="truncate">{r.title ?? 'Open card'}</span>
            </span>
            {(summary.length > 0 || r.orgName) && (
              <span className="truncate pl-4 text-[10px] text-muted-foreground/70">
                {summary.join(' · ')}
                {/* The Conductor holds ceo.cross_org. A receipt that cannot tell
                    him a card landed in the wrong client's workspace hides the
                    one error worth catching, so the org is always named. */}
                {r.orgName ? `${summary.length ? ' · ' : ''}${r.orgName}` : ''}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}

/**
 * ⚠ MEMOISED, and it ships in the same commit as the markdown renderer.
 *
 * handleStreamEvent does `setTurns(prev => [...prev])` on EVERY `text` event.
 * Without this comparator a 40-turn conversation re-parses all 40 markdown
 * bodies on every delta, and "the new version is slow" is a worse complaint
 * than the one being fixed. Modelled on messageRowPropsAreEqual
 * (session-chat-pane.tsx:5145).
 *
 * `status` is in the comparison deliberately: a row flipping streaming → error
 * must repaint, or a cut-off turn silently stops looking cut off.
 */
export const TurnRow = React.memo(TurnRowImpl, (a, b) => {
  if (a.turn.ts !== b.turn.ts) return false;
  if (a.turn.role !== b.turn.role) return false;
  if (a.turn.content !== b.turn.content) return false;
  if (a.showReceipts !== b.showReceipts) return false;
  if (a.streaming !== b.streaming) return false;
  const at = a.turn.role === 'assistant' ? a.turn : null;
  const bt = b.turn.role === 'assistant' ? b.turn : null;
  if ((at?.toolCalls?.length ?? 0) !== (bt?.toolCalls?.length ?? 0)) return false;
  if (at?.status !== bt?.status) return false;
  return conductorArtifactsSignature(at?.artifacts) === conductorArtifactsSignature(bt?.artifacts);
});
TurnRow.displayName = 'TurnRow';
